import cn from '../../../lib/classNames';

import {
	categoryMeta,
	faviconUrl,
	Finding,
	SEVERITY_LABEL,
	SOURCE_TIER_LABEL,
	sourceHost,
	SourceTier,
	sourceTier,
} from './watchdogUtils';

import styles from './Watchdog.module.scss';

function SourceLink({ url, tier }: { url: string; tier: SourceTier }) {
	const host = sourceHost(url);
	if (!host) {
		return null;
	}
	const favicon = faviconUrl(url);

	return (
		<a className={styles.source} href={url} target="_blank" rel="noopener noreferrer" title={url}>
			{favicon && <img className={styles['source-favicon']} src={favicon} alt="" loading="lazy" />}
			{host}
			<span className={cn(styles['source-tier'], styles[`source-tier--${tier}`])}>
				{SOURCE_TIER_LABEL[tier]}
			</span>
		</a>
	);
}

export default function FindingCard({ finding }: { finding: Finding }) {
	const { severity, category } = finding;
	const meta = categoryMeta(category);
	const tier = sourceTier(finding.sourceUrl);

	return (
		<article
			className={cn(styles.finding, styles[`finding--${severity}`], {
				[styles['finding--resolved']]: finding.resolved,
			})}
		>
			<div className={styles.badges}>
				{SEVERITY_LABEL[severity] && (
					<span className={cn(styles['severity-badge'], styles[`severity-badge--${severity}`])}>
						{SEVERITY_LABEL[severity]}
					</span>
				)}
				<span
					className={styles['category-badge']}
					style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}
				>
					<span className="material-icons">{meta.icon}</span>
					{meta.label}
				</span>
				{finding.isNew && !finding.resolved && <span className={styles['new-badge']}>New</span>}
				{finding.resolved && <span className={styles['resolved-badge']}>Resolved</span>}
				{finding.eventDateLabel && (
					<span className={styles['finding-date']}>{finding.eventDateLabel}</span>
				)}
			</div>

			<h4 className={styles['finding-title']}>{finding.title}</h4>

			{finding.summary && <p className={styles['finding-text']}>{finding.summary}</p>}
			{finding.body && <p className={styles['finding-text']}>{finding.body}</p>}

			{finding.affectedAreas.length > 0 && (
				<p className={styles['finding-meta']}>
					<strong>Affected:</strong> {finding.affectedAreas.join(', ')}
				</p>
			)}
			{finding.operationalImpact && (
				<p className={styles['finding-impact']}>
					<strong>Impact:</strong> {finding.operationalImpact}
				</p>
			)}
			{finding.specificAction && (
				<p className={styles['finding-action']}>
					<strong>Action:</strong> {finding.specificAction}
				</p>
			)}

			<div className={styles.sources}>
				<SourceLink url={finding.sourceUrl} tier={tier} />
				{finding.mergedSources.map((source) => (
					<SourceLink key={source.url} url={source.url} tier={source.tier} />
				))}
				{finding.item.channel === 'openai' && (
					<span className={styles['channel-note']}>AI web search</span>
				)}
			</div>
		</article>
	);
}
